// react core
import { useState, useContext, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";

// material design
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import Typography from "@mui/material/Typography";

// entzy components
import configEntzy from "components/config/ConfigEntzy";
import { MainContext } from "components/main/MainContext";
import { serviceGraphCall } from "services/graphql/call";
import { serviceObjectDownload } from "services/storage/object";
import { userGet } from "hooks/identity/identityState";
import { ActionLoader } from "components/utils/common/CommonLoaders";
import { scrollSnapTop, scrollTopDone } from "models/Tools";
import MainModuleDrawer from "components/main/MainModuleDrawer";
import CatchNotFound from "components/catch/CatchNotFound";

// fonts and icons
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose as iconClose } from "@fortawesome/pro-thin-svg-icons";

function MainModule(props) {
  const user = props.user;
  const navigate = props.navigate;

  // encode any landing props to pass into module config
  let landing = props.landing;
  if (landing) {
    landing = encodeURIComponent(JSON.stringify(landing));
  }

  let { module } = useParams();
  const moduleName = module ? module : props.module ? props.module : "core";
  const moduleUrl =
    configEntzy.APP_MODULES_URL +
    moduleName +
    (configEntzy.IS_LOCAL ? "/" : "/latest/");

  const iframeRef = useRef(null);

  const [version, setVersion] = useState(null);
  const [height, setHeight] = useState("0px");
  const [frameLoaded, setFrameLoaded] = useState(false);
  const [drawerContent, setDrawerContent] = useState({ open: false });

  const mainContext = useContext(MainContext);
  const moduleState =
    mainContext && mainContext.state && mainContext.state.moduleExchange
      ? mainContext.state.moduleExchange[moduleName]
      : null;

  const updateModuleExchange = async (comms) => {
    mainContext.updateModuleExchange(comms);
  };
  const cbUpdateModuleExchange = useCallback(updateModuleExchange, [
    mainContext,
  ]);

  const handleFrameLoaded = () => {
    setFrameLoaded(true);
  };

  // const handleOpenEventuator = async (name) => {
  //   mainContext.updateViewerTrail({
  //     joinTab: 1,
  //     calendarView: false,
  //     openEvent: name,
  //   });
  // };
  // const cbHandleOpenEventuator = useCallback(handleOpenEventuator, [
  //   mainContext,
  // ]);

  const handleSetTrail = async (trail) => {
    if (!trail) trail = { menuSourcePage: moduleName };
    mainContext.updateViewerTrail(trail);
  };
  const cbHandleSetTrail = useCallback(handleSetTrail, [
    mainContext,
    moduleName,
  ]);

  const handleSetLocation = async (location) => {
    if (!location || !location.type || !location.data) return;
    mainContext.updateLocation(location);
  };
  const cbHandleSetLocation = useCallback(handleSetLocation, [mainContext]);

  const handleSetHelper = async (helper) => {
    const closedHelper = { open: false, section: null, subSection: null };
    if (!helper) helper = closedHelper;
    if (mainContext.state.helper.open) helper = closedHelper;
    mainContext.updateHelper(helper);
    scrollSnapTop();
  };
  const cbHandleSetHelper = useCallback(handleSetHelper, [mainContext]);

  const handleToggleDrawer = (content) => {
    setDrawerContent({
      open: content && content.open ? true : false,
      ...content,
    });
  };
  const cbHandleToggleDrawer = useCallback(handleToggleDrawer, []);

  const validateComms = (comms) => {
    let parentOrigin = configEntzy.APP_URL;
    let moduleOrigin = moduleUrl;
    let localOrigin = "localhost";
    let validComms = false;

    // interpret sender and receiver
    let sender = comms.sender || window.location.href;
    if (sender === "parent") {
      sender = parentOrigin;
    } else if (sender === "module") {
      sender = moduleOrigin;
    }
    let receiver = comms.receiver || window.location.href;
    if (receiver === "parent") {
      receiver = parentOrigin;
    } else if (receiver === "module") {
      receiver = moduleOrigin;
    }
    let senderUrl = new URL(sender);
    let receiverUrl = new URL(receiver);

    // validate comms based on origins
    if (senderUrl.origin === receiverUrl.origin) {
      validComms = true;
    }
    if (
      senderUrl.hostname === localOrigin &&
      receiverUrl.hostname === localOrigin
    ) {
      validComms = true;
    }
    if (
      senderUrl.origin === parentOrigin &&
      receiverUrl.origin === moduleOrigin
    ) {
      validComms = true;
    }
    if (
      senderUrl.origin === moduleOrigin &&
      receiverUrl.origin === parentOrigin
    ) {
      validComms = true;
    }
    // if (!validComms) {
    //   console.warn("Skipping comms (parent). Not module frame related.");
    //   console.warn("S: ", senderUrl.origin, "R: ", receiverUrl.origin);
    // }

    // output results and config
    return {
      valid: validComms,
      parentOrigin: parentOrigin,
      moduleOrigin: moduleOrigin,
    };
  };
  const cbValidateComms = useCallback(validateComms, [moduleUrl]);

  // effect to fetch module version called on an interval basis
  useEffect(() => {
    const fetchModuleVersion = async (indexUrl) => {
      const CALL_MINS = configEntzy.IS_LOCAL ? 1 : 10;
      const ts = Math.floor(
        new Date().getTime() / (CALL_MINS * 60 * 1000)
      ).toString();
      const url = indexUrl + "?t=" + ts;
      const res = await fetch(url, { cache: "no-store" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, "text/html");
      const meta = doc.querySelector('meta[name="module-version"]');
      return meta?.getAttribute("content") ?? null;
    };

    const loadVersion = async () => {
      try {
        let modVersion = await fetchModuleVersion(moduleUrl);
        if (!modVersion) modVersion = "0.0.0";
        if (modVersion === "__VERSION__") modVersion = "0.0.0";
        setVersion(modVersion);

        if (!configEntzy.IS_PRODUCTION) {
          console.log(
            `Module version: ${modVersion} (env: ${configEntzy.APP_ENV}, region: ${configEntzy.APP_REGION})`
          );
          console.log(`Module URL: ${moduleUrl}`);
        }
      } catch (error) {
        console.error("Error fetching module version:", error);
      }
    };
    loadVersion();
  }, [moduleUrl]);

  // effect for messages comms between module iframe
  useEffect(() => {
    const iframe = iframeRef.current;

    // traverse an object and its children to find matching keys
    // provide the action to take when a match is found
    function traverseObject(value, parent, keyset) {
      if (Array.isArray(value)) {
        for (let item of value) {
          traverseObject(item, value, keyset);
        }
      } else if (value && typeof value === "object") {
        for (let k of Object.keys(value)) {
          for (let keyItem of keyset) {
            if (keyItem.key === k) {
              const parentKey = Object.keys(parent).find(
                (key) => parent[key] === value
              );
              keyItem.callback({
                key: k,
                value: value[k],
                parent: parent,
                parentKey: parentKey,
              });
            }
          }
          traverseObject(value[k], value, keyset);
        }
      }
    }

    function checkForMockOverride(moduleName, action, body, response) {
      if (moduleName === "tribe") {
        if (action.type === "query" && action.name === "searchUsersSignal") {
          if (!response.data) {
            response = {
              data: {
                items: [
                  {
                    user: { UserId: "mock-user-12345", UserName: "mock_user" },
                    tribe: {
                      TribeId: "mock-tribe-67890",
                      TribeCriteriaCoordinates:
                        "{type=Point, coordinates=[8.5417, 47.3769]}",
                    },
                  },
                ],
                itemsCount: 1,
              },
              success: true,
            };
          }
        }
      }
      return response;
    }

    const receiveMessage = async (event) => {
      let comms = cbValidateComms({
        sender: event.origin,
      });
      if (!comms.valid) return;

      if (!version || !iframe || !iframe.contentWindow) {
        console.warn("Module not ready for comms");
        return;
      }

      let user, request, action, response, message;

      message = null;

      switch (event.data.type) {
        case "frame-resize":
          if (event.data.height) {
            setHeight(event.data.height);
          }

          break;

        case "state-request":
          if (!moduleState) {
            // console.warn("No module state found for:", moduleName);
            return;
          }
          const moduleApiState = moduleState.api;
          if (moduleApiState && moduleApiState.result) {
            message = moduleApiState.result[0];
            message.type = "api-response";
            message.request.stateCache = true;
          }

          break;

        case "api-user":
          user = await userGet();
          request = event.data.request;
          response = { data: user, success: true };
          message = { request, response };
          message.type = "api-response";

          break;

        case "api-set-trail":
          cbHandleSetTrail(event.data.request.body.trail);

          break;

        case "api-set-helper":
          cbHandleSetHelper(event.data.request.body.helper);

          break;

        case "api-set-location":
          cbHandleSetLocation(event.data.request.body.location);

          break;

        case "api-scroll-top":
          scrollSnapTop();

          break;

        case "api-navigate-url":
          if (event.data.request.body.Url.startsWith("/")) {
            await scrollTopDone();
            navigate(event.data.request.body.Url);
          }

          break;

        case "api-graphql":
          user = await userGet();
          request = event.data.request;
          action = {
            type: request.mutation ? "mutation" : "query",
            name: request.mutation ? request.mutation : request.query,
          };
          response = await serviceGraphCall(
            action.type,
            action.name,
            request.body
          );
          // response mocking for testing
          if (configEntzy.IS_LOCAL) {
            response = checkForMockOverride(
              moduleName,
              action,
              request.body,
              response
            );
          }
          // handle images if requested
          if (
            request.images &&
            Array.isArray(request.images) &&
            request.images.length &&
            response.data &&
            response.data.items
          ) {
            // create a set of keys to look for with callback function
            const imageKeys = [];
            const imageFields = request.images.map((img) => {
              return {
                key: img.field,
                callback: async (params) => {
                  const imageSize = img.size ? img.size : "md";
                  if (
                    params.value &&
                    !params.value.includes("none") &&
                    !imageKeys.includes(params.value)
                  ) {
                    imageKeys.push({
                      key: params.value,
                      size: imageSize,
                    });
                  }
                },
              };
            });
            traverseObject(response.data.items, null, imageFields);
            if (imageKeys.length) {
              let imageCache = {};
              await Promise.all(
                imageKeys.map(async (img) => {
                  if (!imageCache[img.key]) {
                    let imageValue = await serviceObjectDownload(
                      img.key,
                      img.size,
                      user.identity
                    );
                    if (!imageValue) {
                      imageValue = {
                        data: "/modules/core/src/images/banners/crowd-t50.png",
                        success: false,
                      };
                    }
                    imageCache[img.key] = imageValue;
                  }
                })
              );
              // replace all matching keys in response with downloaded image data
              const replaceImageFields = request.images.map((img) => {
                return {
                  key: img.field,
                  callback: async (params) => {
                    if (params.value && imageCache[params.value]) {
                      params.parent[params.parentKey][params.key] =
                        imageCache[params.value];
                    }
                  },
                };
              });
              traverseObject(response.data.items, null, replaceImageFields);
            }
          }
          // cache the module request response for reuse
          cbUpdateModuleExchange({
            moduleName: moduleName,
            type: "api",
            action: "result",
            result: {
              request: request,
              response: response,
            },
          });
          // send response back to iframe
          message = { request, response };
          message.type = "api-response";

          break;

        case "api-graphql-clear":
          cbUpdateModuleExchange({
            moduleName: moduleName,
            type: "api",
            action: "result",
            result: {
              request: request,
              response: null,
            },
          });

          break;

        case "api-drawer-open":
          cbHandleToggleDrawer({
            open: true,
            ...event.data.request.body,
          });

          break;

        default:
          break;
      }

      if (message && iframe.contentWindow) {
        iframe.contentWindow.postMessage(message, comms.moduleOrigin);
      }
    };

    window.addEventListener("message", receiveMessage);
    return () => window.removeEventListener("message", receiveMessage);
  }, [
    moduleUrl,
    moduleName,
    moduleState,
    version,
    navigate,
    cbValidateComms,
    cbUpdateModuleExchange,
    cbHandleSetTrail,
    cbHandleSetHelper,
    cbHandleSetLocation,
    cbHandleToggleDrawer,
  ]);

  return (
    <Box className="box-default">
      {user && user.connected ? (
        <Box className="box-default">
          {(!version || !frameLoaded) && (
            <Box className="box-default">
              <ActionLoader size="md" />
            </Box>
          )}
          {version && (
            <Box className="box-default">
              <iframe
                ref={iframeRef}
                id={moduleName}
                title={moduleName}
                src={
                  moduleUrl + "?v=" + version + (landing ? "&l=" + landing : "")
                }
                style={{
                  width: "100%",
                  height: height,
                  border: "none",
                  margin: 0,
                  display: "block",
                  overflow: "hidden",
                }}
                scrolling="no"
                onLoad={handleFrameLoaded}
              ></iframe>
            </Box>
          )}
          <Drawer
            anchor="bottom"
            open={drawerContent.open}
            onClose={() => cbHandleToggleDrawer()}
          >
            <Box
              className="box-default text-right"
              sx={{
                p: configEntzy.APP_SPACING_MD,
              }}
            >
              <Typography variant="subtitle2">
                <FontAwesomeIcon
                  icon={iconClose}
                  size="2x"
                  className="action-pointer"
                  onClick={() => cbHandleToggleDrawer()}
                />
              </Typography>
            </Box>
            <Box className="box-default">
              <MainModuleDrawer {...props} content={drawerContent} />
            </Box>
          </Drawer>
        </Box>
      ) : (
        <CatchNotFound {...props} />
      )}
    </Box>
  );
}

export default MainModule;
